/** @jsxImportSource @emotion/react */

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { css } from "@emotion/react";
import UI from "@mile-meeting/mile-ui";
import { AgreeContainer, BtnContainer, Container, FormSection, InputForm, Line, SpanEssential } from "./index.styles";
import VerifyEmail from "pages/auth/component/verify-email";
import PasswordInput from "pages/auth/component/password-input/password-input";
import { validatePasswordFormat } from "utils/validation";
import { sendEmailVerificatoin, verifyEmailCode } from "apis/auth/email-verification";
import { registerUserWithMile } from "apis/auth/sign-up";
import AlertModal from "uis/modal/alert-modal";
import { acceptInviteMember } from "apis/member-invites/member-invites";
import { openGooglePopup } from "./google-oauth-modal";

const validatePasswordMatch = (pw?: string, comparePW?: string) => {
  if (pw === comparePW) return true;
  else return false;
};

const BasicSignUp = ({
  code,
  redirectGoogleSignup,
}: {
  code: string | null;
  redirectGoogleSignup?: (email: string, accessToken: string) => void;
}) => {
  const navigate = useNavigate();
  const [pw, setPw] = useState<string>();
  const [rePw, setRePw] = useState<string>();
  const [successVerifyEmail, setSuccessVerifyEmail] = useState<boolean>(false);
  const [name, setName] = useState<string>();
  const [infoAgree, setInfoAgree] = useState<boolean>(false);
  const [useAgree, setUseAgree] = useState<boolean>(false);
  const [email, setEmail] = useState<string>();
  const [emailVerificationToken, setEmailVerificationToken] = useState<string>();
  const [showLoginAlertModal, setShowLoginAlertModal] = useState<boolean>(false);

  const sendRequestVerifyEmail = async (email: string) => {
    const res = await sendEmailVerificatoin(email);
    if (res?.statusCode === 409) {
      setShowLoginAlertModal(true);
    }
    return { success: res?.success ?? false, statusCode: res?.statusCode ?? 400, message: res?.message ?? "" };
  };

  const verifyCode = async (email: string, code: string) => {
    const res = await verifyEmailCode(email, code);
    if (res?.success) {
      setEmail(email);
      setSuccessVerifyEmail(true);
      setEmailVerificationToken(res.emailVerificationToken);
    }

    return { success: res?.success ?? false, message: res?.message ?? "" };
  };

  const enabledSignUpBtn = () => {
    if (
      successVerifyEmail &&
      infoAgree &&
      useAgree &&
      pw &&
      rePw &&
      name &&
      validatePasswordFormat(pw) &&
      validatePasswordMatch(pw, rePw)
    )
      return true;
    else return false;
  };

  const onHandleSignup = async () => {
    if (email && name && pw) {
      const res = await registerUserWithMile(email, name, pw, infoAgree, useAgree, emailVerificationToken);
      if (res?.success) {
        if (code) {
          await acceptInviteMember(code);
        }
        navigate("/login", { state: { signupSuccess: true } });
      } else {
        if (res?.statusCode === 409) {
          setShowLoginAlertModal(true);
        }
      }
    }
  };

  return (
    <Container>
      <UI.Typography type="title01" fontStyle="bold" color="var(--alias-color-label-normal)">
        회원가입
      </UI.Typography>
      <InputForm>
        <VerifyEmail onHandleSendRequestVerifyEmail={sendRequestVerifyEmail} onHandleVerifyCode={verifyCode} />
        <FormSection>
          <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
            비밀번호
            <SpanEssential>*</SpanEssential>
          </UI.Typography>
          <PasswordInput
            placeholder="비밀번호"
            showHelpText={true}
            onChange={setPw}
            helpText="최소 8자 이상, 영문/숫자/특수문자의 조합으로 설정해 주세요."
            error={pw ? (!validatePasswordFormat(pw) ? true : false) : false}
          />
        </FormSection>
        <FormSection>
          <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
            비밀번호 확인
            <SpanEssential>*</SpanEssential>
          </UI.Typography>
          <PasswordInput
            placeholder="비밀번호 확인"
            onChange={setRePw}
            error={rePw ? (!validatePasswordMatch(pw, rePw) ? true : false) : false}
            helpText="비밀번호가 일치하지 않습니다."
            showHelpText={rePw ? (!validatePasswordMatch(pw, rePw) ? true : false) : false}
          />
        </FormSection>
        <FormSection>
          <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
            사용자 이름
            <SpanEssential>*</SpanEssential>
          </UI.Typography>
          <UI.Input placeholder="사용자 이름" onChange={setName} />
        </FormSection>
      </InputForm>
      <AgreeContainer>
        <div
          css={css`
            display: flex;
            gap: 4px;
          `}
        >
          <UI.CheckBox size="medium" onChange={setInfoAgree} selected={infoAgree} />
          <UI.Typography
            style={{ cursor: "pointer", display: "flex", gap: 4 }}
            type="label01"
            fontStyle="medium"
            color="var(--alias-color-label-neutral)"
          >
            <p
              style={{ textDecoration: "underline" }}
              onClick={() => window.open("https://policy.mile.im/privacy/2024.10.25.html")}
            >
              개인정보 수집 및 이용 동의
            </p>
            <SpanEssential>(필수)</SpanEssential>
          </UI.Typography>
        </div>
        <div
          css={css`
            display: flex;
            gap: 4px;
          `}
        >
          <UI.CheckBox size="medium" onChange={setUseAgree} selected={useAgree} />
          <UI.Typography
            style={{ cursor: "pointer", display: "flex", gap: 4 }}
            type="label01"
            fontStyle="medium"
            color="var(--alias-color-label-neutral)"
          >
            <p
              style={{ textDecoration: "underline" }}
              onClick={() => window.open("https://policy.mile.im/service-agree/2024.11.01.html")}
            >
              이용약관 동의
            </p>
            <SpanEssential>(필수)</SpanEssential>
          </UI.Typography>
        </div>
      </AgreeContainer>
      <BtnContainer>
        <UI.Button
          size="large"
          title="회원가입"
          disabled={!enabledSignUpBtn()}
          onClick={() => {
            if (enabledSignUpBtn()) onHandleSignup();
          }}
        />
        <Line />
        <UI.Button
          onClick={() => openGooglePopup(redirectGoogleSignup)}
          size="large"
          title="구글 계정으로 회원가입"
          primary={false}
          type="outlined"
          prefixIcon="google"
          showPrefixIcon
        />
      </BtnContainer>
      <AlertModal
        title="이미 가입된 이메일입니다."
        btnTitle="로그인 하러 가기"
        onChange={() => (window.location.href = "/login")}
        modalIsOpen={showLoginAlertModal}
        closeModal={() => setShowLoginAlertModal(!showLoginAlertModal)}
      />
    </Container>
  );
};

export default BasicSignUp;
